import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, CheckCircle, AlertCircle, ChevronDown, ChevronRight,
  FileText, MapPin, Calendar, DollarSign, Users, Trash2, Image, Link as LinkIcon,
  Building2, Shield, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SectionHeader({ title, icon: Icon, completion, open, onToggle, testId }) {
  const pct = completion || 0;
  const color = pct === 100 ? "text-emerald-400" : pct > 0 ? "text-amber-400" : "text-slate-500";
  return (
    <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-800/50 transition-colors" data-testid={testId}>
      <div className="flex items-center gap-3">
        <Icon size={18} className="text-teal-400" />
        <span className="text-sm font-semibold text-white">{title}</span>
        <span className={`text-xs font-mono ${color}`}>{pct}%</span>
      </div>
      <div className="flex items-center gap-2">
        {pct === 100 ? <CheckCircle size={14} className="text-emerald-400" /> : pct > 0 ? <AlertCircle size={14} className="text-amber-400" /> : null}
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </div>
    </button>
  );
}

function FileField({ label, fieldName, value, opId, onUploaded, testId }) {
  const [uploading, setUploading] = useState(false);
  const hasFile = value && value.storage_key;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/operations/${opId}/sub-elements/upload/${fieldName}`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${label} subido`);
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir archivo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testId}>
      <Label className="text-slate-400 text-xs mb-1 block">{label}</Label>
      {hasFile ? (
        <div className="flex items-center gap-2 bg-emerald-500/5 border border-emerald-500/20 rounded-lg px-3 py-2">
          <FileText size={14} className="text-emerald-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-emerald-300 truncate">{value.filename}</p>
            <p className="text-[10px] text-slate-500">{(value.size_bytes / 1024).toFixed(0)} KB &middot; {new Date(value.uploaded_at).toLocaleDateString("es-MX")}</p>
          </div>
          <label className="cursor-pointer text-xs text-slate-400 hover:text-white">
            Reemplazar
            <input type="file" className="hidden" onChange={handleUpload} />
          </label>
        </div>
      ) : (
        <label className={`flex items-center justify-center gap-2 border border-dashed rounded-lg px-3 py-3 cursor-pointer transition-colors ${uploading ? "border-teal-500/40 bg-teal-500/5" : "border-slate-700 hover:border-slate-500 hover:bg-slate-800/30"}`}>
          {uploading ? <Loader2 size={14} className="animate-spin text-teal-400" /> : <Upload size={14} className="text-slate-500" />}
          <span className="text-xs text-slate-400">{uploading ? "Subiendo..." : "Seleccionar archivo"}</span>
          <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

export default function OperationDetail() {
  const { operationId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState({});
  const [states, setStates] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [openSections, setOpenSections] = useState({ s1: true, s2: false, s3: false, s4: false, s5: false });

  const load = useCallback(async () => {
    try {
      const [subRes, statesRes] = await Promise.all([
        axios.get(`${API}/operations/${operationId}/sub-elements`, { withCredentials: true }),
        axios.get(`${API}/operations/geo/states`, { withCredentials: true }),
      ]);
      setData(subRes.data);
      const d = subRes.data.detail || {};
      setFields({
        property_address: d.property_address || "",
        electronic_folio: d.electronic_folio || "",
        state: d.state || "",
        municipality: d.municipality || "",
        first_notice_validity_start: d.first_notice_validity_start || "",
        first_notice_validity_end: d.first_notice_validity_end || "",
        acquisition_date: d.acquisition_date || "",
        second_notice_validity_start: d.second_notice_validity_start || "",
        second_notice_validity_end: d.second_notice_validity_end || "",
        official_cadastral_value: d.official_cadastral_value || "",
        declared_cadastral_value: d.declared_cadastral_value || "",
        property_verification_link: d.property_verification_link || "",
        operation_amount: d.operation_amount || "",
        appraisal_amount: d.appraisal_amount || "",
        appraisal_validity_start: d.appraisal_validity_start || "",
        appraisal_validity_end: d.appraisal_validity_end || "",
      });
      setStates(statesRes.data.states || []);
      if (d.state) {
        const mRes = await axios.get(`${API}/operations/geo/municipalities/${d.state}`, { withCredentials: true });
        setMunicipalities(mRes.data.municipalities || []);
      }
    } catch { toast.error("Error cargando sub-elementos"); }
    setLoading(false);
  }, [operationId]);

  useEffect(() => { load(); }, [load]);

  const handleStateChange = async (stateId) => {
    setFields({ ...fields, state: stateId, municipality: "" });
    try {
      const res = await axios.get(`${API}/operations/geo/municipalities/${stateId}`, { withCredentials: true });
      setMunicipalities(res.data.municipalities || []);
    } catch { setMunicipalities([]); }
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/operations/${operationId}/sub-elements`, fields, { withCredentials: true });
      toast.success("Datos guardados");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const [sellers, setSellers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [savingParticipants, setSavingParticipants] = useState(false);

  useEffect(() => {
    if (data?.detail) {
      setSellers(data.detail.sellers || []);
      setBuyers(data.detail.buyers || []);
    }
  }, [data]);

  const saveParticipants = async () => {
    setSavingParticipants(true);
    try {
      await axios.put(`${API}/operations/${operationId}/participants`, { sellers, buyers }, { withCredentials: true });
      toast.success("Participantes actualizados");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar participantes");
    } finally {
      setSavingParticipants(false);
    }
  };

  const toggleParticipant = (customerId, role) => {
    if (role === "seller") {
      if (buyers.includes(customerId)) { toast.error("Ya es comprador"); return; }
      setSellers(prev => prev.includes(customerId) ? prev.filter(id => id !== customerId) : [...prev, customerId]);
    } else {
      if (sellers.includes(customerId)) { toast.error("Ya es vendedor"); return; }
      setBuyers(prev => prev.includes(customerId) ? prev.filter(id => id !== customerId) : [...prev, customerId]);
    }
  };

  const deletePhoto = async (index) => {
    try {
      await axios.delete(`${API}/operations/${operationId}/sub-elements/photo/${index}`, { withCredentials: true });
      toast.success("Foto eliminada");
      load();
    } catch { toast.error("Error al eliminar foto"); }
  };

  const toggle = (s) => setOpenSections({ ...openSections, [s]: !openSections[s] });

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
      </div>
    </Layout>
  );

  const detail = data?.detail || {};
  const op = data?.operation || {};
  const customers = data?.customers || [];
  const completion = detail.completion_status || {};
  const fc = "bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500";

  const custName = (c) => c.person_type === "moral" ? (c.legal_name || "—") : `${c.first_name || ""} ${c.paternal_surname || ""}`.trim() || "—";

  return (
    <Layout>
      <div data-testid="operation-detail-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/operations" className="text-slate-400 hover:text-white"><ArrowLeft size={20} /></Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white" data-testid="op-detail-heading">
              {op.operation_type_name || "Operacion"} — {op.operation_number}
            </h1>
            <p className="text-slate-400 text-sm">
              Expediente digital &middot; Monto: ${Number(op.amount_mxn || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-xs text-slate-500">Avance general</p>
              <p className="text-lg font-bold text-teal-400">{completion.overall || 0}%</p>
            </div>
            <Button onClick={saveFields} disabled={saving} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="save-fields-btn">
              {saving ? "Guardando..." : "Guardar Datos"}
            </Button>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-2">

          {/* SECTION 1 — Property & Registry */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <SectionHeader title="1. Inmueble y Registro" icon={Building2} completion={completion.property_registry} open={openSections.s1} onToggle={() => toggle("s1")} testId="section-1-header" />
            {openSections.s1 && (
              <div className="px-5 pb-5 pt-2 space-y-4 border-t border-slate-800">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FileField label="Escritura Antecedente (PDF)" fieldName="previous_deed_file" value={detail.previous_deed_file} opId={operationId} onUploaded={load} testId="field-previous-deed" />
                  <FileField label="Boleta de Registro del Antecedente" fieldName="registry_receipt_file" value={detail.registry_receipt_file} opId={operationId} onUploaded={load} testId="field-registry-receipt" />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Direccion del Inmueble</Label>
                  <textarea rows={2} className={`${fc} w-full rounded-md border px-3 py-2 text-sm`} value={fields.property_address}
                    onChange={(e) => setFields({ ...fields, property_address: e.target.value })} placeholder="Direccion completa del inmueble" data-testid="field-property-address" />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Folio Real Electronico</Label>
                  <Input className={fc} value={fields.electronic_folio} onChange={(e) => setFields({ ...fields, electronic_folio: e.target.value })}
                    placeholder="Ej: 20120200000000308125" data-testid="field-electronic-folio" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Estado de la Republica</Label>
                    <Select value={fields.state} onValueChange={handleStateChange}>
                      <SelectTrigger className={fc} data-testid="field-state"><SelectValue placeholder="Seleccionar estado" /></SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                        {states.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Municipio</Label>
                    <Select value={fields.municipality} onValueChange={(v) => setFields({ ...fields, municipality: v })} disabled={!fields.state}>
                      <SelectTrigger className={fc} data-testid="field-municipality"><SelectValue placeholder="Seleccionar municipio" /></SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                        {municipalities.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* SECTION 2 — Preventive Notice & Certificates */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <SectionHeader title="2. Avisos Preventivos y Certificados" icon={Calendar} completion={completion.preventive_notices} open={openSections.s2} onToggle={() => toggle("s2")} testId="section-2-header" />
            {openSections.s2 && (
              <div className="px-5 pb-5 pt-2 space-y-4 border-t border-slate-800">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FileField label="Proyecto Primer Aviso Preventivo" fieldName="first_preventive_notice_file" value={detail.first_preventive_notice_file} opId={operationId} onUploaded={load} testId="field-first-notice" />
                  <FileField label="Proyecto Solicitud CLG" fieldName="freedom_liens_request_file" value={detail.freedom_liens_request_file} opId={operationId} onUploaded={load} testId="field-clg-request" />
                  <FileField label="Boleta Primer Aviso Preventivo" fieldName="first_notice_receipt_file" value={detail.first_notice_receipt_file} opId={operationId} onUploaded={load} testId="field-first-notice-receipt" />
                  <FileField label="Boleta CLG (Certificado de Libertad de Gravamenes)" fieldName="freedom_liens_receipt_file" value={detail.freedom_liens_receipt_file} opId={operationId} onUploaded={load} testId="field-clg-receipt" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Vigencia Primer Aviso — Inicio</Label>
                    <Input type="date" className={fc} value={fields.first_notice_validity_start}
                      onChange={(e) => setFields({ ...fields, first_notice_validity_start: e.target.value })} data-testid="field-notice1-start" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Vigencia Primer Aviso — Fin</Label>
                    <Input type="date" className={fc} value={fields.first_notice_validity_end}
                      onChange={(e) => setFields({ ...fields, first_notice_validity_end: e.target.value })} data-testid="field-notice1-end" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Fecha de Adquisicion</Label>
                  <Input type="date" className={fc} value={fields.acquisition_date}
                    onChange={(e) => setFields({ ...fields, acquisition_date: e.target.value })} data-testid="field-acquisition-date" />
                </div>
                <FileField label="Boleta Segundo Aviso Preventivo" fieldName="second_notice_receipt_file" value={detail.second_notice_receipt_file} opId={operationId} onUploaded={load} testId="field-second-notice-receipt" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Vigencia Segundo Aviso — Inicio</Label>
                    <Input type="date" className={fc} value={fields.second_notice_validity_start}
                      onChange={(e) => setFields({ ...fields, second_notice_validity_start: e.target.value })} data-testid="field-notice2-start" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Vigencia Segundo Aviso — Fin</Label>
                    <Input type="date" className={fc} value={fields.second_notice_validity_end}
                      onChange={(e) => setFields({ ...fields, second_notice_validity_end: e.target.value })} data-testid="field-notice2-end" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* SECTION 3 — Catastral & Valuation */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <SectionHeader title="3. Datos Catastrales y Avaluo" icon={DollarSign} completion={completion.catastral_valuation} open={openSections.s3} onToggle={() => toggle("s3")} testId="section-3-header" />
            {openSections.s3 && (
              <div className="px-5 pb-5 pt-2 space-y-4 border-t border-slate-800">
                <FileField label="Cedula Catastral del Año en Curso" fieldName="cadastral_certificate_file" value={detail.cadastral_certificate_file} opId={operationId} onUploaded={load} testId="field-cadastral-cert" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Valor Catastral Oficial (MXN)</Label>
                    <Input type="number" step="0.01" className={fc} value={fields.official_cadastral_value}
                      onChange={(e) => setFields({ ...fields, official_cadastral_value: e.target.value })} placeholder="0.00" data-testid="field-cadastral-value" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Valor Catastral Declarado (MXN)</Label>
                    <Input type="number" step="0.01" className={fc} value={fields.declared_cadastral_value}
                      onChange={(e) => setFields({ ...fields, declared_cadastral_value: e.target.value })} placeholder="0.00" data-testid="field-declared-value" />
                  </div>
                </div>

                {/* Property Photos */}
                <div data-testid="field-property-photos">
                  <Label className="text-slate-400 text-xs mb-1 block">Fotos del Inmueble</Label>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-2">
                    {(detail.property_photos || []).map((p, i) => (
                      <div key={i} className="relative bg-slate-800 rounded-lg p-2 group">
                        <div className="flex items-center gap-2">
                          <Image size={14} className="text-teal-400 shrink-0" />
                          <p className="text-[10px] text-slate-300 truncate">{p.filename}</p>
                        </div>
                        <button onClick={() => deletePhoto(i)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 size={12} className="text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <FileField label="Agregar Foto" fieldName="property_photos" value={null} opId={operationId} onUploaded={load} testId="field-add-photo" />
                </div>

                <div>
                  <Label className="text-slate-400 text-xs">Link de Verificacion del Inmueble</Label>
                  <div className="flex items-center gap-2">
                    <LinkIcon size={14} className="text-slate-500 shrink-0" />
                    <Input className={fc} value={fields.property_verification_link}
                      onChange={(e) => setFields({ ...fields, property_verification_link: e.target.value })}
                      placeholder="https://..." data-testid="field-verification-link" />
                  </div>
                </div>

                <FileField label="Contrato Previo y Comprobantes de Pago" fieldName="prior_promise_sell_file" value={detail.prior_promise_sell_file} opId={operationId} onUploaded={load} testId="field-prior-contract" />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Monto de Operacion (MXN)</Label>
                    <Input type="number" step="0.01" className={fc} value={fields.operation_amount}
                      onChange={(e) => setFields({ ...fields, operation_amount: e.target.value })} placeholder="0.00" data-testid="field-operation-amount" />
                    <p className="text-[10px] text-slate-500 mt-0.5">Se sincroniza con monto principal de la operacion</p>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Monto del Avaluo (MXN)</Label>
                    <Input type="number" step="0.01" className={fc} value={fields.appraisal_amount}
                      onChange={(e) => setFields({ ...fields, appraisal_amount: e.target.value })} placeholder="0.00" data-testid="field-appraisal-amount" />
                  </div>
                </div>

                <FileField label="Avaluo (PDF)" fieldName="appraisal_file" value={detail.appraisal_file} opId={operationId} onUploaded={load} testId="field-appraisal-file" />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Vigencia Avaluo — Inicio</Label>
                    <Input type="date" className={fc} value={fields.appraisal_validity_start}
                      onChange={(e) => setFields({ ...fields, appraisal_validity_start: e.target.value })} data-testid="field-appraisal-start" />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Vigencia Avaluo — Fin</Label>
                    <Input type="date" className={fc} value={fields.appraisal_validity_end}
                      onChange={(e) => setFields({ ...fields, appraisal_validity_end: e.target.value })} data-testid="field-appraisal-end" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* SECTION 4 — Tax & Municipal Compliance */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <SectionHeader title="4. Cumplimiento Fiscal y Municipal" icon={Shield} completion={completion.tax_compliance} open={openSections.s4} onToggle={() => toggle("s4")} testId="section-4-header" />
            {openSections.s4 && (
              <div className="px-5 pb-5 pt-2 space-y-4 border-t border-slate-800">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FileField label="Recibo de Pago Predial" fieldName="property_tax_receipt_file" value={detail.property_tax_receipt_file} opId={operationId} onUploaded={load} testId="field-predial" />
                  <FileField label="Carta No Adeudo Predial" fieldName="no_property_tax_letter_file" value={detail.no_property_tax_letter_file} opId={operationId} onUploaded={load} testId="field-no-adeudo-predial" />
                  <FileField label="No Adeudo de Agua (CAPA)" fieldName="no_water_bill_file" value={detail.no_water_bill_file} opId={operationId} onUploaded={load} testId="field-capa" />
                  <FileField label="Cuotas Condominales al Corriente" fieldName="condo_fees_clearance_file" value={detail.condo_fees_clearance_file} opId={operationId} onUploaded={load} testId="field-condo-fees" />
                  <FileField label="Cooperacion por Obra Publica" fieldName="public_works_receipt_file" value={detail.public_works_receipt_file} opId={operationId} onUploaded={load} testId="field-public-works" />
                  <FileField label="No Adeudo Cooperacion Obra" fieldName="no_public_works_debt_file" value={detail.no_public_works_debt_file} opId={operationId} onUploaded={load} testId="field-no-public-works-debt" />
                  <FileField label="Proyecto ISABI (Impuesto Traslativo de Dominio)" fieldName="transfer_tax_project_file" value={detail.transfer_tax_project_file} opId={operationId} onUploaded={load} testId="field-isabi" />
                </div>
              </div>
            )}
          </div>

          {/* SECTION 5 — Participants */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <SectionHeader title="5. Comparecientes (Vendedores / Compradores)" icon={Users} completion={completion.participants} open={openSections.s5} onToggle={() => toggle("s5")} testId="section-5-header" />
            {openSections.s5 && (
              <div className="px-5 pb-5 pt-2 border-t border-slate-800">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Sellers */}
                  <div data-testid="sellers-panel">
                    <h4 className="text-sm font-semibold text-white mb-2">Vendedores ({sellers.length})</h4>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {customers.map((c) => {
                        const isSeller = sellers.includes(c.customer_id);
                        const isBuyer = buyers.includes(c.customer_id);
                        return (
                          <button key={c.customer_id} onClick={() => toggleParticipant(c.customer_id, "seller")}
                            disabled={isBuyer}
                            className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isSeller ? "bg-teal-500/10 border border-teal-500/30 text-teal-300" :
                              isBuyer ? "bg-slate-800/50 text-slate-600 cursor-not-allowed" :
                              "bg-slate-800/30 border border-slate-700/50 text-slate-400 hover:border-slate-600"
                            }`} data-testid={`seller-toggle-${c.customer_id}`}>
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSeller ? "bg-teal-500 border-teal-500" : "border-slate-600"}`}>
                              {isSeller && <CheckCircle size={10} className="text-white" />}
                            </div>
                            <span className="truncate">{custName(c)}</span>
                            {c.rfc && <span className="text-[10px] text-slate-500 ml-auto shrink-0">{c.rfc}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Buyers */}
                  <div data-testid="buyers-panel">
                    <h4 className="text-sm font-semibold text-white mb-2">Compradores ({buyers.length})</h4>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {customers.map((c) => {
                        const isBuyer = buyers.includes(c.customer_id);
                        const isSeller = sellers.includes(c.customer_id);
                        return (
                          <button key={c.customer_id} onClick={() => toggleParticipant(c.customer_id, "buyer")}
                            disabled={isSeller}
                            className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isBuyer ? "bg-sky-500/10 border border-sky-500/30 text-sky-300" :
                              isSeller ? "bg-slate-800/50 text-slate-600 cursor-not-allowed" :
                              "bg-slate-800/30 border border-slate-700/50 text-slate-400 hover:border-slate-600"
                            }`} data-testid={`buyer-toggle-${c.customer_id}`}>
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isBuyer ? "bg-sky-500 border-sky-500" : "border-slate-600"}`}>
                              {isBuyer && <CheckCircle size={10} className="text-white" />}
                            </div>
                            <span className="truncate">{custName(c)}</span>
                            {c.rfc && <span className="text-[10px] text-slate-500 ml-auto shrink-0">{c.rfc}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <Button onClick={saveParticipants} disabled={savingParticipants} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="save-participants-btn">
                    {savingParticipants ? "Guardando..." : "Guardar Participantes"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
